import * as apiConstants from './apiUtilsConstants';
import { callUIAPI, isSuccessCode } from './apiUtils';

export const saveContent = async (entityName, recordId, contentGenerationType, content, generatedContentId) => {
  try {
    return callUIAPI(apiConstants.entityAPIRoutes[entityName] + '/fileUploadURL/', { recordId, contentGenerationType, generatedContentId }, apiConstants.METHOD_POST, true)
      .then((fileMetaData) => {
        console.log('storageApiUtils.saveContent - fileMetaData:', fileMetaData);
        const presignedURL = fileMetaData?.data?.uploadURL?.presignedURL;
        return fetch(presignedURL, {
          method: 'PUT',
          body: content,
          headers: {
            'Content-Type': 'text/html',
          },
        })
          .then(response => {
            console.log('storageApiUtils.saveContent - uploadContentInS3 - response:', response);
            return { message: 'Content saved successfully', id: recordId, updatedOn: response.updatedOn };
          });
      });
  } catch (error) {
    console.error('storageApiUtils.saveContent - error:', error);
    throw error;
  }
};

const getFileUploadURL = async (entityName, fileName, contentType, record) => {
  return callUIAPI(apiConstants.entityAPIRoutes[entityName] + '/fileUploadURL', { fileName, contentType, input: record }, apiConstants.METHOD_POST, true)
    .then(response => response.data);
};

export const uploadFileInS3 = async (entityName, file, record) => {
  const fileName = file.name;
  const contentType = file.type;

  return getFileUploadURL(entityName, fileName, contentType, record)
    .then((fileMetaData) => {
      return fetch(fileMetaData.presignedURL, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`File upload failed: ${response.status} ${response.statusText}`);
          }
          return { fullFileName: fileMetaData.key, id: fileMetaData.id, updatedOn: fileMetaData.updatedOn };
        });
    });
};

export const getFileDownloadURL = async (entityName, id) => {
  return callUIAPI(apiConstants.entityAPIRoutes[entityName] + `/${id}/fileURL`, null, apiConstants.METHOD_GET, true)
    .then(response => response.data);
};

const getFileDownloadURLOld = async (entityName, id) => {
  return callUIAPI(apiConstants.entityAPIRoutes[entityName] + `/${id}/fileURLOld`, null, apiConstants.METHOD_GET, true)
    .then(response => response.data);
};

const getGeneratedFileDownloadURLs = (entityName, id) => {
  return callUIAPI(apiConstants.entityAPIRoutes[entityName] + `/${id}/generatedFileURLs`, null, apiConstants.METHOD_GET, true)
    .then(response => response.data);
};

export const getContentFromS3 = (entityName, id) => {
  return getFileDownloadURL(entityName, id)
    .then(response => {
      const presignedURL = response.downloadURL;
      if (!presignedURL) {
        console.error('storageApiUtils.getContentFromS3 - No presigned URL found');
        return Promise.resolve({ content: '', timestamp: null });
      }
      console.log('storageApiUtils.getContentFromS3 - presignedURL:', presignedURL);
      return fetch(presignedURL, { method: 'GET' });
    })
    .then(response => {
      if (response === '') return { content: '', timestamp: null };
      if (!isSuccessCode(response.status)) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }
      const lastModified = response.headers?.get?.('last-modified') ?? null;
      return response.text().then(textContent => {
        try {
          const jsonContent = JSON.parse(textContent);
          if (jsonContent && typeof jsonContent === 'object' && 'content' in jsonContent) {
            return { ...jsonContent, lastModified };
          }
          return { content: textContent, timestamp: null, lastModified };
        } catch {
          console.log('storageApiUtils.getContentFromS3 - Content is in old text format, returning as text');
          return { content: textContent, timestamp: null, lastModified };
        }
      });
    });
};

export const getContentFromS3Old = (entityName, id) => {
  return getFileDownloadURLOld(entityName, id)
    .then(response => {
      const presignedURL = response.downloadURL;
      if (!presignedURL) {
        console.error('storageApiUtils.getContentFromS3Old - No presigned URL found');
        return Promise.resolve('');
      }
      console.log('storageApiUtils.getContentFromS3Old - presignedURL:', presignedURL);
      return fetch(presignedURL, { method: 'GET' });
    })
    .then(response => {
      if (response === '') return '';
      if (!isSuccessCode(response.status)) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }
      return response.text();
    });
};

export const getGeneratedContentsFromS3 = (entityName, id) => {
  return getGeneratedFileDownloadURLs(entityName, id)
    .then(response => {
      console.log('storageApiUtils.getGeneratedContentsFromS3 - response:', response);
      const downloadURLs = response.downloadURLs;
      if (!downloadURLs || downloadURLs.length === 0) {
        console.error('storageApiUtils.getGeneratedContentsFromS3 - No download URLs found');
        return [];
      }
      console.log('storageApiUtils.getGeneratedContentsFromS3 - downloadURLs:', downloadURLs);
      const fetchPromises = downloadURLs.map(file => {
        if (!file.downloadURL) {
          console.error(`storageApiUtils.getGeneratedContentsFromS3 - No download URL for file ID: ${file.generatedContentId}`);
          return Promise.resolve(null);
        }
        return fetch(file.downloadURL, { method: 'GET' })
          .then(response => {
            if (!isSuccessCode(response.status)) {
              throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
            }
            return response.json();
          })
          .then(storedItem => {
            if (!storedItem || typeof storedItem !== 'object' || Array.isArray(storedItem)) {
              return storedItem;
            }
            return { ...storedItem, lastModified: file.lastModified };
          })
          .catch(error => {
            console.error(`storageApiUtils.getGeneratedContentsFromS3 - Error fetching file ID ${file.generatedContentId}:`, error);
            return null;
          });
      });
      return Promise.all(fetchPromises).then(results => results.filter(item => item !== null));
    })
    .catch(error => {
      console.error('storageApiUtils.getGeneratedContentsFromS3 - Error fetching generated contents:', error);
      return [];
    });
};

export const uploadEditorImage = async (dataUrl) => {
  console.log('storageApiUtils.uploadEditorImage');
  return callUIAPI('images/editor/upload', { dataUrl }, apiConstants.METHOD_POST, true)
    .then(response => {
      console.log('storageApiUtils.uploadEditorImage - response:', response);
      return response.data;
    });
};

export const generateEditorImageCaption = async (url) => {
  console.log('storageApiUtils.generateEditorImageCaption');
  return callUIAPI('images/editor/caption', { url }, apiConstants.METHOD_POST, true)
    .then(response => {
      console.log('storageApiUtils.generateEditorImageCaption - response:', response);
      return response.data;
    });
};

export const uploadEditorVideo = async (dataUrl) => {
  console.log('storageApiUtils.uploadEditorVideo');
  return callUIAPI('videos/editor/upload', { dataUrl }, apiConstants.METHOD_POST, true)
    .then(response => {
      console.log('storageApiUtils.uploadEditorVideo - response:', response);
      return response.data;
    });
};

export const getEditorImages = async () => {
  console.log('storageApiUtils.getEditorImages');
  return callUIAPI('images/editor/recent', null, apiConstants.METHOD_GET, true)
    .then(response => {
      console.log('storageApiUtils.getEditorImages - response:', response);
      return response.data.list || [];
    });
};

export const deleteEditorImage = async (key) => {
  console.log('storageApiUtils.deleteEditorImage - key:', key);
  return callUIAPI('images/editor/delete', { key }, apiConstants.METHOD_POST, true)
    .then(response => {
      console.log('storageApiUtils.deleteEditorImage - response:', response);
      return response.data;
    });
};

export const getDateFormatLookupList = async () => {
  const response = await callUIAPI('common/dateFormats', null, apiConstants.METHOD_GET, false);
  return response.data.list;
};

export const getTimeZonesLookupList = async () => {
  const response = await callUIAPI('common/timeZones', null, apiConstants.METHOD_GET, false);
  return response.data.list;
};